<template>
  <div class="unposted-widget">
    <div v-if="loading" class="text-center py-4">
      <v-progress-circular indeterminate size="28" color="orange darken-1" />
    </div>
    <div v-else-if="fetchError" class="text-center py-4">
      <v-icon color="grey lighten-1" size="32">{{ icons.mdiAlertCircleOutline }}</v-icon>
      <p class="text-caption grey--text mt-2">Unable to load data</p>
    </div>
    <div v-else class="kpi-card-body">
      <!-- Gradient background accent -->
      <div class="kpi-gradient-bg" :class="count > 0 ? 'kpi-gradient-warn' : 'kpi-gradient-ok'" />

      <!-- Icon + number row -->
      <div class="d-flex align-center justify-space-between mb-4">
        <div class="kpi-icon-wrap" :class="count > 0 ? 'kpi-icon-warn' : 'kpi-icon-ok'">
          <v-icon size="32" :color="toneColor(count > 0 ? 'orange' : 'green')">
            {{ count > 0 ? icons.mdiClockAlertOutline : icons.mdiCashCheck }}
          </v-icon>
        </div>
        <div class="text-right">
          <div class="kpi-number" :class="toneText(count > 0 ? 'orange' : 'green')">
            {{ count }}
          </div>
          <div class="text-caption grey--text font-weight-medium">Unposted</div>
        </div>
      </div>

      <!-- Status line -->
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon
            x-small
            :color="toneColor(count > 0 ? 'orange' : 'green')"
            class="mr-1"
          >
            {{ count > 0 ? icons.mdiClockAlertOutline : icons.mdiCheckCircleOutline }}
          </v-icon>
          <span
            class="text-caption font-weight-medium"
            :class="toneText(count > 0 ? 'orange' : 'green')"
          >
            {{ count > 0 ? 'Action needed' : 'All up to date' }}
          </span>
        </div>
        <v-btn
          v-if="count > 0"
          x-small
          depressed
          rounded
          color="orange darken-1"
          dark
          @click="goToPayments"
          class="kpi-action-btn"
        >
          View
          <v-icon x-small right>{{ icons.mdiArrowRight }}</v-icon>
        </v-btn>
      </div>

      <!-- Divider + posting period -->
      <v-divider class="my-3" />
      <div class="d-flex align-center">
        <span class="text-caption grey--text">
          {{ postingPeriod }}
        </span>
      </div>

      <!-- Outstanding approvals (payment audit records awaiting approval) -->
      <v-divider class="my-3" />
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon
            x-small
            :color="toneColor(pendingApprovals > 0 ? 'deep-orange' : 'green')"
            class="mr-1"
          >
            {{ pendingApprovals > 0 ? icons.mdiClipboardAlertOutline : icons.mdiClipboardCheckOutline }}
          </v-icon>
          <div class="d-flex flex-column approval-info">
            <span
              class="text-caption font-weight-medium"
              :class="pendingApprovals > 0 ? toneText('deep-orange') : 'grey--text'"
            >
              {{ pendingApprovals }} outstanding {{ pendingApprovals === 1 ? 'approval' : 'approvals' }}
            </span>
            <span
              v-if="approvalDatesLabel"
              class="text-caption grey--text approval-dates"
            >
              {{ approvalDatesLabel }}
            </span>
          </div>
        </div>
        <v-btn
          v-if="pendingApprovals > 0"
          x-small
          depressed
          rounded
          color="deep-orange darken-1"
          dark
          @click="goToApprovals"
          class="kpi-action-btn"
        >
          Review
          <v-icon x-small right>{{ icons.mdiArrowRight }}</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>

import {
  mdiClockAlertOutline,
  mdiCheckCircleOutline,
  mdiArrowRight,
  mdiCashCheck,
  mdiAlertCircleOutline,
  mdiClipboardAlertOutline,
  mdiClipboardCheckOutline,
} from '@mdi/js';
import { clients } from '../../../util/clients';
import WidgetThemeMixin from '../WidgetTheme.mixin';

const { backendRest } = clients.direct;

// The server sends calendar days as 'YYYY-MM-DD'; the system standard reads MM/DD/YYYY.
const isoToStandardDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(`${iso}`);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : `${iso}`;
};

export default {
  name: 'UnpostedPaymentsWidget',
  mixins: [WidgetThemeMixin],
  data: () => ({
    icons: {
      mdiClockAlertOutline,
      mdiCheckCircleOutline,
      mdiArrowRight,
      mdiCashCheck,
      mdiAlertCircleOutline,
      mdiClipboardAlertOutline,
      mdiClipboardCheckOutline,
    },
    count: 0,
    pendingApprovals: 0,
    pendingApprovalDates: [],
    postingPeriod: '',
    loading: true,
    fetchError: false,
  }),
  computed: {
    approvalDatesLabel() {
      if (!this.pendingApprovalDates.length) return '';
      return this.pendingApprovalDates.map(isoToStandardDate).join(', ');
    },
  },
  async created() {
    await this.fetchCount();
  },
  methods: {
    async fetchCount() {
      this.loading = true;
      try {
        const resp = await backendRest.get(
          '/dashboard/unposted-payments',
        );
        const data = resp.data || {};
        this.count = data.total || 0;
        this.pendingApprovals = data.pendingApprovals || 0;
        this.pendingApprovalDates = data.pendingApprovalDates || [];
        const pp = data.postingPeriod;
        if (pp) {
          this.postingPeriod = [pp.from, pp.to].map(isoToStandardDate).join(' - ');
        }
      } catch (e) {
        console.error('Failed to fetch unposted payments count', e);
        this.fetchError = true;
        this.count = 0;
        this.pendingApprovals = 0;
        this.pendingApprovalDates = [];
      } finally {
        this.loading = false;
      }
    },
    goToPayments() {
      this.$router.push({ name: 'Tax - Payments - Posting Period' });
    },
    goToApprovals() {
      this.$router.push({ name: 'Tax - Payments - Audit' });
    },
    async refresh() {
      await this.fetchCount();
    },
  },
};
</script>

<style scoped>
.unposted-widget {
  position: relative;
}

.kpi-card-body {
  padding: 4px 0;
  position: relative;
}

.kpi-gradient-bg {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  opacity: 0.18;
  pointer-events: none;
}

.kpi-gradient-warn {
  background: radial-gradient(circle, #ff9800 0%, transparent 70%);
}

.kpi-gradient-ok {
  background: radial-gradient(circle, #43a047 0%, transparent 70%);
}

.kpi-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kpi-icon-warn {
  background: var(--widget-tint-warn, linear-gradient(135deg, #fff8e1, #ffe0b2));
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.25);
}

.kpi-icon-ok {
  background: var(--widget-tint-ok, linear-gradient(135deg, #e8f5e9, #c8e6c9));
  box-shadow: 0 4px 16px rgba(67, 160, 71, 0.2);
}

.kpi-number {
  font-size: 2.8rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -2px;
}

.kpi-action-btn {
  font-size: 11px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 26px !important;
}

.approval-info {
  min-width: 0;
}

.approval-dates {
  word-break: break-word;
  line-height: 1.35;
}
</style>
